import { cloneDeep, get } from 'lodash';
import { MutationTree } from 'vuex';
import { State, defaultHeatmapSliderOptions, defaultSettings } from './state';
import { BookViewMutationTypes } from './mutation-types';
import { config } from '@/services/config';
import {
  BookApiOptions,
  ClientApiResponseResults,
  HeatmapSliderOptions,
} from './types';
import { authState } from '@/services/integrations/auth0';
import { UserRole } from '@/enums/UserRole';
import { ClientHeatmapDataItem } from '@/types/Client';

export type Mutations<S = State> = {
  [BookViewMutationTypes.BOOK_STORE_SET_DATA](
    state: S,
    payload: {
      clientData: Array<ClientApiResponseResults>;
      totalResultCount: number;
    }
  ): void;
  [BookViewMutationTypes.BOOK_STORE_CLEAR_DATA](state: S): void;
  [BookViewMutationTypes.BOOK_STORE_SET_LOADING](
    state: S,
    isLoading: boolean
  ): void;
  [BookViewMutationTypes.BOOK_STORE_SET_HEATMAP_DATA](
    state: S,
    payload: {
      data: Array<ClientHeatmapDataItem>;
    }
  ): void;
  [BookViewMutationTypes.BOOK_STORE_SET_HEATMAP_LOADING](
    state: S,
    isLoading: boolean
  ): void;
  [BookViewMutationTypes.BOOK_STORE_SET_SETTINGS](
    state: S,
    settings: BookApiOptions
  ): void;
  [BookViewMutationTypes.BOOK_STORE_SET_SLIDER_OPTIONS](
    state: S,
    sliderOptions: HeatmapSliderOptions
  ): void;
  [BookViewMutationTypes.BOOK_STORE_RESET_DATA](state: S): void;
};

export const mutations: MutationTree<State> & Mutations = {
  [BookViewMutationTypes.BOOK_STORE_SET_DATA](
    state: State,
    payload: {
      clientData: Array<ClientApiResponseResults>;
      totalResultCount: number;
    }
  ) {
    state.client.clientData = payload.clientData;
    state.client.totalResultCount = payload.totalResultCount;
  },
  [BookViewMutationTypes.BOOK_STORE_CLEAR_DATA](state: State) {
    state.client.clientData = [];
    state.client.totalResultCount = 0;
  },
  [BookViewMutationTypes.BOOK_STORE_SET_LOADING](
    state: State,
    isLoading: boolean
  ) {
    state.client.isLoading = isLoading;
  },
  [BookViewMutationTypes.BOOK_STORE_SET_HEATMAP_DATA](
    state: State,
    payload: {
      data: Array<ClientHeatmapDataItem>;
    }
  ) {
    state.heatmap.heatmapData = payload.data;
  },
  [BookViewMutationTypes.BOOK_STORE_SET_HEATMAP_LOADING](
    state: State,
    isLoading: boolean
  ) {
    state.heatmap.isLoading = isLoading;
  },
  [BookViewMutationTypes.BOOK_STORE_SET_SETTINGS](state, settings) {
    // Only store filters for bankers
    if (!authState.userRoles.value.includes(UserRole.BANKER)) {
      settings.filters = [];
    }
    state.settings = settings;
  },
  [BookViewMutationTypes.BOOK_STORE_SET_SLIDER_OPTIONS](
    state: State,
    sliderOptions: HeatmapSliderOptions
  ) {
    state.heatmap.heatmapSliderOptions = sliderOptions;
  },
  [BookViewMutationTypes.BOOK_STORE_RESET_DATA](state: State) {
    const settingOverrides: BookApiOptions = get(
      config.layout,
      'bookView.settingOverrides',
      {}
    );
    state.heatmap.heatmapSliderOptions = { ...defaultHeatmapSliderOptions };
    state.settings = cloneDeep({ ...defaultSettings, ...settingOverrides });
  },
};
